/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {ReactElement} from 'react';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {AugmentedSmarterTrailerSensorData, SensorTypes} from '@ion/core/src/types/entities';
import {dateFormatter, timeFormatter} from '@ion/core/src/utils/DateUtil';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {calculateTotalZoneAvailability} from '@ion/features/src/smarterTrailer/SmarterTrailerUtils';
import {SmarterTrailerWidgetInfo} from '../../types/types';
import {getTimeDifference} from './helper';
import WidgetContainer from './WidgetContainer';
import WidgetDescription from './WidgetDescription';

export type CargoWidgetProps = {
  timestamp: SmarterTrailerWidgetInfo;
  trailerData: AugmentedSmarterTrailerSensorData;
  sensorType?: SensorTypes;
  icon: ReactElement
};

/**
 * Defines the Cargo Sensor Widget component.
 *
 * ```typescript
 *
 * import {CargoSenseWidget} from '@ion/dls/src/components/smarterTrailer';
 *
 * <CargoSenseWidget trailerData={trailerData} />
 *
 * ```
 */
export const CargoSenseWidget = ({
  timestamp,
  trailerData,
  icon
}: CargoWidgetProps) => {
  if (timestamp) {
    const {CargoSense: cargoSenseData} = trailerData || {};
    const {0: {messageTime: timeStamp}} = cargoSenseData;
    const today       = getTimeDifference(timestamp);
    const messageDate = dateFormatter(timeStamp, UserPreferences.getSystemDateFormat());
    const messageTime = isNil(today) ? timeFormatter(timeStamp, UserPreferences.getSystemTimeFormat()) : today;
    const space       = calculateTotalZoneAvailability(cargoSenseData);
    const label       = translate('common:smarterTrailer.cargoSense.widgetLabel');
    const style       = {color: dlsColor('grey2')};
    const value       = !isNil(space)
      ? translate('common:smarterTrailer.cargoSense.percentAvail', {percentAvail: space})
      : translate('common:notAvailable');

    return (
      <WidgetContainer>
        {icon}
        <WidgetDescription label={label}
          value={value}
          messageTime={messageTime}
          messageDate={messageDate}
          valueStyle={style} />
      </WidgetContainer>
    );
  }
  return null;
};

export default CargoSenseWidget;
